import React, { useCallback, useEffect, useMemo, useState } from "react";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { toast } from "sonner";
import { simulationRunSavedAsStrategyVersionToastCopy } from "../../../utils/simulateQuotaToasts";
import { useHistory, useParams } from "react-router-dom";
import { useShadcnInsetDynamicPageTitle } from "@/hooks/useShadcnInsetBreadcrumbTrail";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import CardHeader from "../../../components/Card/CardHeader";
import { NotesPanel } from "../../../components/Notes/NotesPanel";
import { PineScriptCodeBlock } from "../../../components/Reports/PineScriptCodeBlock";
import {
  BacktestsApiError,
  deleteBacktest,
  getBacktestSlim,
  getBacktestWithSnapshot,
  getBacktestTradeIds,
  patchBacktest,
} from "../../../services/backtestsApi";
import type { Backtest } from "../../../types/backtests";
import { timezoneFromBacktestRow } from "../../../utils/backtestTimezone";
import { MetricColumn, formatMetricValue } from "../ExecutionRulesSimulator/MetricColumn";
import { ResearchDisclaimerNotice } from "../../../components/ResearchDisclaimerNotice";
import {
  createReportStrategyPolicyVersion,
  getReport,
  getReportStrategyDetail,
  getReportStrategyPolicyVersionPineScript,
  ReportStrategyPineCompileError,
} from "../../../services/reportsApi";
import TradeReviewTab from "../TradeLog/components/TradeReview/TradeReviewTab";
import type { Trade } from "../../../types/trade";
import { backendTradeToFrontendTrade } from "../../../services/tradeStorage";
import { formatIanaTimezoneWithUtcOffset } from "../../../utils/reportWindowLabel";
import { isBacktestSavedAsPolicyVersion } from "../Strategies/strategyDetailUtils";
import {
  SIMULATOR_LIST_PATH,
  simulatorResultsWithBacktestPath,
} from "../ExecutionRulesSimulator/simulatorRoutes";

type RouteParams = { backtestId: string };

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

type PolicyRuleNode = { kind: "rule"; op: string; path: string; value: unknown };
type PolicyGroupNode = { kind: "group"; op: "and" | "or"; children: PolicyNode[] };
type PolicyNode = PolicyRuleNode | PolicyGroupNode;
type PolicyPayload = { action?: "veto" | "keep_only" | string; root: PolicyNode };

function isPolicyNode(v: unknown): v is PolicyNode {
  if (!isPlainObject(v)) return false;
  const kind = (v as any).kind;
  if (kind === "rule") return typeof (v as any).path === "string" && typeof (v as any).op === "string" && "value" in (v as any);
  if (kind === "group") return ((v as any).op === "and" || (v as any).op === "or") && Array.isArray((v as any).children);
  return false;
}

function coercePolicyPayload(v: unknown): PolicyPayload | null {
  if (!isPlainObject(v)) return null;
  const root = (v as any).root;
  if (!isPolicyNode(root)) return null;
  return { action: (v as any).action, root };
}

const OP_WORD: Record<string, string> = { eq: "is", ne: "is not", in: "is one of", gte: "≥", lte: "≤" };

const PATH_LABEL: Record<string, string> = {
  "regime.market_condition": "Market condition",
  "regime.direction": "Direction (long lookback, TF-scaled)",
  "regime.direction_short": "Direction (short lookback, TF-scaled)",
  "trade.trade_type": "Trade type",
  "volatility.atr_regime": "ATR regime",
  "trend_location.price_vs_ma.sma_200": "Price vs SMA200",
};

function formatRuleValue(v: unknown): string {
  if (Array.isArray(v)) return v.map((x) => String(x)).join(", ");
  if (typeof v === "number") return v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  if (v == null) return "—";
  return String(v);
}

function oneLineRule(r: PolicyRuleNode): string {
  const label = PATH_LABEL[r.path] ?? r.path;
  const op = OP_WORD[r.op] ?? r.op;
  return `${label} ${op} ${formatRuleValue(r.value)}`;
}

function flattenAndClauses(node: PolicyNode): string[] {
  if (node.kind === "rule") return [oneLineRule(node)];
  if (node.op === "and") return node.children.flatMap(flattenAndClauses);
  // For nested OR inside an AND context, keep it grouped.
  const parts = node.children.map((c) => summarizeNodeOneLine(c)).filter(Boolean);
  return parts.length ? [`Either (${parts.join(" OR ")})`] : [];
}

function summarizeNodeOneLine(node: PolicyNode): string {
  if (node.kind === "rule") return oneLineRule(node);
  const join = node.op === "and" ? " AND " : " OR ";
  const parts = node.children.map((c) => summarizeNodeOneLine(c)).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `(${parts.join(join)})`;
}

function summarizeTopLevel(payload: PolicyPayload): { headline: string; groups: string[][] } {
  const action = payload.action === "keep_only" ? "Keep only" : "Veto";
  const headline =
    payload.action === "keep_only"
      ? "Keep only trades that match these conditions"
      : "Veto (exclude) trades that match these conditions";
  const root = payload.root;
  if (root.kind === "group" && root.op === "or") {
    const groups = root.children.map((c) => flattenAndClauses(c)).filter((g) => g.length > 0);
    return { headline: `${action}: ${headline}`, groups };
  }
  return { headline: `${action}: ${headline}`, groups: [flattenAndClauses(root)] };
}

export default function BacktestDetail(): JSX.Element {
  const { backtestId } = useParams<RouteParams>();
  const history = useHistory();
  const textColor = useColorModeValue("gray.700", "gray.200");
  const subtleText = useColorModeValue("gray.500", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const evidencePreBg = useColorModeValue("gray.50", "rgba(255,255,255,0.06)");
  const evidencePreBorder = useColorModeValue("gray.200", "gray.600");
  // Chakra polymorphic props can trigger “union too complex” IDE diagnostics in big TSX trees.
  const BadgeAny = Badge as any;
  const BoxAny = Box as any;
  const FlexAny = Flex as any;
  const HStackAny = HStack as any;
  const ButtonAny = Button as any;
  const CollapseAny = Collapse as any;
  const SimpleGridAny = SimpleGrid as any;
  const StackAny = Stack as any;
  const HeadingAny = Heading as any;
  const TextAny = Text as any;
  const SpinnerAny = Spinner as any;
  const AlertAny = Alert as any;
  const AlertIconAny = AlertIcon as any;
  const AlertDescriptionAny = AlertDescription as any;
  const InputAny = Input as any;
  const DividerAny = Divider as any;
  const VStackAny = VStack as any;

  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backtest, setBacktest] = useState<Backtest | null>(null);
  /** Legacy trade-review rows — loaded on demand (~1.2 MB audit bundle). */
  const [auditBacktest, setAuditBacktest] = useState<Backtest | null>(null);

  const [title, setTitle] = useState("");
  useShadcnInsetDynamicPageTitle(title.trim() || backtest?.title?.trim() || null);
  const [reportId, setReportId] = useState("");
  const [reportStrategyId, setReportStrategyId] = useState<string>("");
  const [strategyDetailLoading, setStrategyDetailLoading] = useState(false);
  const [strategyDetailError, setStrategyDetailError] = useState<string | null>(null);
  const [strategyDetail, setStrategyDetail] = useState<any>(null);
  const [selectedPolicyVersionId, setSelectedPolicyVersionId] = useState<string>("");
  const [pineLoading, setPineLoading] = useState(false);
  const [pineError, setPineError] = useState<string | null>(null);
  const [pineCompileUnsupportedPaths, setPineCompileUnsupportedPaths] = useState<string[]>([]);
  const [pineCompileUnsupportedOps, setPineCompileUnsupportedOps] = useState<string[]>([]);
  const [pineScript, setPineScript] = useState<string>("");
  const [tradeIdsLoading, setTradeIdsLoading] = useState(false);
  const [tradeIdsError, setTradeIdsError] = useState<string | null>(null);
  const [tradeIds, setTradeIds] = useState<{ kept: string[]; removed: string[]; source?: string } | null>(null);
  /** Default on: Trade Review is for policy validation; trade-ids load once with the page. */
  const [overlayEnabled, setOverlayEnabled] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const b = await getBacktestSlim(backtestId);
      setBacktest(b);
      setTitle(b.title || "");
      setReportId(b.report_id || "");
    } catch (e) {
      setBacktest(null);
      setError(e instanceof Error ? e.message : "Failed to load simulator.");
    } finally {
      setLoading(false);
    }
  }, [backtestId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setAuditBacktest(null);
    if (!backtestId) return;
    let cancelled = false;
    void getBacktestWithSnapshot(backtestId)
      .then((row) => {
        if (!cancelled) setAuditBacktest(row);
      })
      .catch(() => {
        if (!cancelled) setAuditBacktest(null);
      });
    return () => {
      cancelled = true;
    };
  }, [backtestId]);

  /** Standalone simulator saves use the unified results UI, not this legacy detail page. */
  useEffect(() => {
    if (loading || !backtest?.id) return;
    const reportLinked =
      (typeof backtest.report_id === "string" && backtest.report_id.trim()) ||
      (typeof reportId === "string" && reportId.trim());
    if (reportLinked) return;
    history.replace(simulatorResultsWithBacktestPath(backtest.id));
  }, [backtest, history, loading, reportId]);

  useEffect(() => {
    setTradeIds(null);
    setTradeIdsError(null);
    setOverlayEnabled(true);
  }, [backtestId]);

  const linkedReportId = useMemo(() => {
    const fromField = reportId.trim();
    if (fromField) return fromField;
    const fromBacktest = backtest?.report_id;
    return typeof fromBacktest === "string" && fromBacktest.trim() ? fromBacktest.trim() : "";
  }, [backtest?.report_id, reportId]);

  useEffect(() => {
    let cancelled = false;
    async function run(): Promise<void> {
      if (!linkedReportId) {
        setReportStrategyId("");
        return;
      }
      try {
        const rep = await getReport(linkedReportId);
        const sid = typeof rep?.strategy_id === "string" ? rep.strategy_id : "";
        if (!cancelled) setReportStrategyId(sid || "");
      } catch {
        if (!cancelled) setReportStrategyId("");
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [linkedReportId]);

  const loadStrategyDetail = useCallback(async () => {
    const strategyId = reportStrategyId;
    if (!strategyId) {
      setStrategyDetail(null);
      setSelectedPolicyVersionId("");
      return;
    }
    setStrategyDetailLoading(true);
    setStrategyDetailError(null);
    try {
      const detail = await getReportStrategyDetail(strategyId);
      setStrategyDetail(detail);
      const currentId = detail?.current_policy_version_id || "";
      const versions = Array.isArray(detail?.policy_versions) ? detail.policy_versions : [];
      const latestId = versions.length ? versions[0]?.id : "";
      setSelectedPolicyVersionId((prev) => prev || currentId || latestId || "");
    } catch (e) {
      setStrategyDetail(null);
      setSelectedPolicyVersionId("");
      setStrategyDetailError(e instanceof Error ? e.message : "Failed to load strategy detail.");
    } finally {
      setStrategyDetailLoading(false);
    }
  }, [reportStrategyId]);

  useEffect(() => {
    void loadStrategyDetail();
    // When strategy context changes, reset pine output so users don’t confuse strategies.
    setPineScript("");
    setPineError(null);
    setPineCompileUnsupportedPaths([]);
    setPineCompileUnsupportedOps([]);
  }, [loadStrategyDetail]);

  const status = backtest?.status || "saved";
  const canSave = !!backtest && !saving;
  const isPolicyVersionSaved = useMemo(
    () => isBacktestSavedAsPolicyVersion(backtest?.id, strategyDetail?.policy_versions),
    [backtest?.id, strategyDetail?.policy_versions],
  );
  const canSaveAsStrategyVersion = Boolean(
    backtest?.id && reportStrategyId && !savingStrategy && !isPolicyVersionSaved,
  );

  const statusBadge = useMemo(() => {
    const scheme = status === "archived" ? "gray" : "green";
    return <BadgeAny colorScheme={scheme}>{status}</BadgeAny>;
  }, [status]);

  const backtestPolicy = useMemo(() => {
    // Prefer top-level persisted policy_tree (current backend shape).
    const top = (backtest as any)?.policy_tree;
    if (top != null) return top;
    // Fallback: try legacy/payload-based shapes.
    const raw = backtest?.payload as unknown;
    if (isPlainObject(raw) && "policy_tree" in raw) return (raw as any).policy_tree;
    return null;
  }, [backtest]);

  const backtestResult = useMemo(() => {
    // Prefer top-level persisted result (current backend shape).
    const top = (backtest as any)?.result;
    if (top != null) return top;
    // Fallback: try legacy/payload-based shapes.
    const raw = backtest?.payload as unknown;
    if (isPlainObject(raw) && "result" in raw) return (raw as any).result;
    return null;
  }, [backtest]);

  const payload = useMemo(() => {
    const raw = backtest?.payload as unknown;
    if (isPlainObject(raw)) return raw as Record<string, unknown>;
    // Some backends store JSON blobs as strings.
    if (typeof raw === "string" && raw.trim()) {
      try {
        const parsed = JSON.parse(raw);
        return isPlainObject(parsed) ? (parsed as Record<string, unknown>) : null;
      } catch {
        return null;
      }
    }
    return null;
  }, [backtest]);

  const tradeReviewRecordedTimezoneLabel = useMemo(() => {
    if (!backtest) return null;
    const iana = timezoneFromBacktestRow(backtest);
    if (!iana) return null;
    const raw = backtest.created_at ? new Date(backtest.created_at) : new Date();
    const ref = Number.isNaN(raw.getTime()) ? new Date() : raw;
    return formatIanaTimezoneWithUtcOffset(iana, ref);
  }, [backtest]);

  const payloadPolicy = useMemo(() => {
    if (!payload) return null;
    // Try a few common shapes without assuming a strict backend schema.
    const direct = payload.policy;
    if (direct != null) return direct;
    const tree = (payload as any).policy_tree;
    if (tree != null) return tree;
    // Sometimes nested under job/request payloads.
    const job = payload.job;
    if (isPlainObject(job) && "request" in job && isPlainObject((job as any).request) && "policy" in (job as any).request) {
      return (job as any).request.policy;
    }
    const req = payload.request;
    if (isPlainObject(req) && "policy" in req) return (req as any).policy;
    const simReq = payload.simulate_request;
    if (isPlainObject(simReq) && "policy" in simReq) return (simReq as any).policy;
    const input = payload.input;
    if (isPlainObject(input) && "policy" in input) return (input as any).policy;
    return null;
  }, [payload]);

  const payloadResult = useMemo(() => {
    if (!payload) return null;
    // Sometimes results are top-level.
    if ("baseline" in payload || "filtered" in payload || "delta" in payload || "coverage" in payload) return payload;
    const direct = payload.result;
    if (direct != null) return direct;
    const simRes = payload.simulate_result;
    if (simRes != null) return simRes;
    const out = payload.output;
    if (out != null) return out;
    const job = payload.job;
    if (isPlainObject(job)) {
      const r = (job as any).result ?? (job as any).output;
      if (r != null) return r;
    }
    return null;
  }, [payload]);

  const shownPolicy = backtestPolicy ?? payloadPolicy;
  const shownResult = backtestResult ?? payloadResult;

  const policyDisplay = useMemo(() => {
    const p = coercePolicyPayload(shownPolicy);
    if (!p) return null;
    return summarizeTopLevel(p);
  }, [shownPolicy]);

  const shownMetrics = useMemo(() => {
    const r = shownResult;
    if (!isPlainObject(r)) return null;
    const baseline = (r as any).baseline;
    const filtered = (r as any).filtered;
    const delta = (r as any).delta;
    const coverage = (r as any).coverage;
    if (baseline == null && filtered == null && delta == null && coverage == null) return null;
    return { baseline, filtered, delta, coverage };
  }, [shownResult]);

  const { isOpen: policyOpen, onToggle: onTogglePolicy } = useDisclosure();
  const { isOpen: rawOpen, onToggle: onToggleRaw } = useDisclosure();
  const { isOpen: pineOpen, onToggle: onTogglePine } = useDisclosure();

  const pretty = useCallback((v: unknown) => {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }, []);

  const onSave = useCallback(async () => {
    if (!backtest) return;
    setSaving(true);
    try {
      const updated = await patchBacktest(backtest.id, {
        title: title.trim() || undefined,
        report_id: reportId.trim() || null,
      });
      setBacktest(updated);
      toast.success("Simulator updated");
    } catch (e) {
      presentMutationError(e, "Update failed");
    } finally {
      setSaving(false);
    }
  }, [backtest, reportId, title]);

  const onSaveAsStrategyVersion = useCallback(async () => {
    if (!backtest?.id || !reportStrategyId) return;
    setSavingStrategy(true);
    try {
      await createReportStrategyPolicyVersion(reportStrategyId, {
        source_backtest_id: backtest.id,
        set_as_current: true,
        policy_row_version: (backtest as any)?.policy_row_version ?? (payload as any)?.policy_row_version ?? undefined,
      });
      // Refresh strategy detail so “current” version + dropdown stays accurate.
      await loadStrategyDetail();
      const strategySavedCopy = simulationRunSavedAsStrategyVersionToastCopy();
      toast.success(strategySavedCopy.title, { description: strategySavedCopy.description });
    } catch (e) {
      presentMutationError(e, "Failed to save strategy version");
    } finally {
      setSavingStrategy(false);
    }
  }, [backtest?.id, loadStrategyDetail, reportStrategyId]);

  const onGeneratePine = useCallback(async () => {
    const strategyId = reportStrategyId;
    const versionId = selectedPolicyVersionId;
    if (!strategyId || !versionId) return;
    const policyRowVersion =
      (backtest as any)?.policy_row_version ?? (payload as any)?.policy_row_version ?? undefined;
    setPineLoading(true);
    setPineError(null);
    setPineCompileUnsupportedPaths([]);
    setPineCompileUnsupportedOps([]);
    try {
      const resp = await getReportStrategyPolicyVersionPineScript(strategyId, versionId, {
        policy_row_version: typeof policyRowVersion === "string" ? policyRowVersion : undefined,
      });
      setPineScript(typeof resp?.pinescript === "string" ? resp.pinescript : "");
    } catch (e) {
      setPineScript("");
      if (e instanceof ReportStrategyPineCompileError) {
        setPineError(e.message);
        setPineCompileUnsupportedPaths(e.unsupported_paths || []);
        setPineCompileUnsupportedOps(e.unsupported_ops || []);
      } else {
        setPineError(e instanceof Error ? e.message : "Failed to generate Pinescript.");
      }
    } finally {
      setPineLoading(false);
    }
  }, [backtest, payload, reportStrategyId, selectedPolicyVersionId]);

  const copyPine = useCallback(async () => {
    if (!pineScript) return;
    try {
      await navigator.clipboard.writeText(pineScript);
      toast.success("Copied Pinescript");
    } catch (e) {
      presentMutationError(e, "Copy failed");
    }
  }, [pineScript]);

  const onToggleArchive = useCallback(async () => {
    if (!backtest) return;
    const next = status === "archived" ? "saved" : "archived";
    setSaving(true);
    try {
      const updated = await patchBacktest(backtest.id, { status: next });
      setBacktest(updated);
      toast.info(next === "archived" ? "Archived" : "Restored");
    } catch (e) {
      presentMutationError(e, "Update failed");
    } finally {
      setSaving(false);
    }
  }, [backtest, status]);

  const onDeleteBacktest = useCallback(async () => {
    if (!backtest) return;
    if (!window.confirm("Delete this simulation permanently? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await deleteBacktest(backtest.id);
      toast.success("Simulator deleted");
      history.push(SIMULATOR_LIST_PATH);
    } catch (e) {
      presentMutationError(e, "Delete failed");
    } finally {
      setDeleting(false);
    }
  }, [backtest, history]);

  const brokerAccountCodeForReview = useMemo(() => {
    return ((backtest as any)?.broker_account_code as string | null) ?? ((shownResult as any)?.meta?.broker_account_code as string | null) ?? null;
  }, [backtest, shownResult]);

  const tradesForReview = useMemo(() => {
    const snapshot = (auditBacktest ?? backtest as any)?.input_snapshot as any;
    const trades = snapshot?.trades;
    if (!Array.isArray(trades)) return [];

    // Backtests often store trades in backend shape (snake_case). Normalize so Trade Review can
    // populate symbols + compute entry dates correctly.
    return (trades as any[]).map((t) => {
      if (t && typeof t === "object" && "entryDate" in t) return t as Trade;
      if (t && typeof t === "object" && ("entry_date" in t || "entry_time" in t)) {
        try {
          // Prefer canonical backend converter (expects entry_date etc).
          return backendTradeToFrontendTrade(t as any);
        } catch {
          // Fallback: minimal mapping for Trade Review filters.
          return {
            id: String((t as any).id ?? (t as any).trade_id ?? ""),
            symbol: String((t as any).symbol ?? ""),
            type: ((t as any).trade_type ?? (t as any).type ?? "buy") as any,
            status: ((t as any).status ?? "closed") as any,
            entryDate: (t as any).entry_date ?? (t as any).entry_time ?? "",
            entryPrice: Number((t as any).entry_price ?? (t as any).entryPrice ?? 0),
            entryQuantity: Number((t as any).entry_quantity ?? (t as any).entryQuantity ?? 0),
            entryFees: Number((t as any).entry_fees ?? (t as any).entryFees ?? 0),
            entryValue: Number((t as any).entry_value ?? (t as any).entryValue ?? 0),
          } as Trade;
        }
      }
      return t as Trade;
    });
  }, [auditBacktest, backtest]);

  const reviewWindow = useMemo(() => {
    const scope = backtest?.scope;
    const meta = (shownResult as any)?.meta as any;
    const tf = scope?.timeframe ?? meta?.timeframe ?? (backtest as any)?.timeframe;
    const localStart =
      scope?.local_start_date ??
      meta?.local_start_date ??
      null;
    const localEnd =
      scope?.local_end_date ??
      meta?.local_end_date ??
      null;
    const tz =
      scope?.timezone ??
      meta?.timezone ??
      tradeReviewRecordedTimezoneLabel?.split(" ")[0] ??
      undefined;

    if (typeof localStart === "string" && typeof localEnd === "string" && localStart && localEnd) {
      return {
        local_start_date: localStart,
        local_end_date: localEnd,
        timezone: typeof tz === "string" ? tz : undefined,
      };
    }

    const start =
      (backtest as any)?.effective_start_date ??
      meta?.start_date ??
      null;
    const end =
      (backtest as any)?.effective_end_date ??
      meta?.end_date ??
      null;
    if (typeof start === "string" && typeof end === "string" && start && end) {
      return { start_date: start, end_date: end };
    }
    if (typeof tf === "string" && tf) return { timeframe: tf };
    return undefined;
  }, [backtest, shownResult, tradeReviewRecordedTimezoneLabel]);

  const fetchBacktestTradeIds = useCallback(async () => {
    if (!backtest?.id) return;
    setTradeIdsLoading(true);
    setTradeIdsError(null);
    try {
      const resp = await getBacktestTradeIds(backtest.id);
      setTradeIds({
        kept: Array.isArray(resp.kept_trade_ids) ? resp.kept_trade_ids : [],
        removed: Array.isArray(resp.removed_trade_ids) ? resp.removed_trade_ids : [],
        source: resp.source,
      });
    } catch (e) {
      // Non-null so auto-fetch effect does not retry in a loop; user can toggle overlay off/on to retry.
      setTradeIds({ kept: [], removed: [], source: undefined });
      if (e instanceof BacktestsApiError && e.status === 404) {
        const d = (e.detail || e.message || "").toLowerCase();
        setTradeIdsError(
          d.includes("not found") && !d.includes("backtest")
            ? "Trade IDs endpoint not found (404). Redeploy the API so GET /api/backtests/{id}/trade-ids is available for simulations."
            : e.detail || e.message || "Simulator not found or not accessible."
        );
      } else {
        setTradeIdsError(e instanceof Error ? e.message : "Failed to load trade ids for overlay.");
      }
    } finally {
      setTradeIdsLoading(false);
    }
  }, [backtest?.id]);

  useEffect(() => {
    if (!backtest?.id || !overlayEnabled || tradeIds) return;
    void fetchBacktestTradeIds();
  }, [backtest?.id, overlayEnabled, tradeIds, fetchBacktestTradeIds]);

  const onToggleOverlay = useCallback(async () => {
    if (!backtest?.id) return;
    const next = !overlayEnabled;
    setOverlayEnabled(next);
    if (!next) return;
    const hasData = !!(tradeIds && (tradeIds.kept.length > 0 || tradeIds.removed.length > 0));
    if (hasData && !tradeIdsError) return;
    await fetchBacktestTradeIds();
  }, [backtest?.id, overlayEnabled, tradeIds, tradeIdsError, fetchBacktestTradeIds]);

  return (
    <BoxAny display="flex" flexDirection="column" pt="120px" px="24px" gap="24px">
      <FlexAny align="flex-start" justify="space-between" gap={3} wrap="wrap">
        <BoxAny>
          <HeadingAny size="lg" color={textColor} fontFamily="'Urbanist', sans-serif">
            Simulator
          </HeadingAny>
          <TextAny color={subtleText} fontSize="sm" fontFamily="mono">
            {backtestId}
          </TextAny>
        </BoxAny>
        <HStackAny>
          <ButtonAny variant="outline" onClick={() => history.push(SIMULATOR_LIST_PATH)}>
            Back to simulator library
          </ButtonAny>
          <ButtonAny variant="outline" onClick={() => history.push(SIMULATOR_LIST_PATH)}>
            Open simulator
          </ButtonAny>
        </HStackAny>
      </FlexAny>

      {loading ? (
        <AlertAny status="info" borderRadius="12px">
          <AlertIconAny />
          <AlertDescriptionAny>
            <HStackAny>
              <SpinnerAny size="sm" />
              <TextAny>Loading simulator…</TextAny>
            </HStackAny>
          </AlertDescriptionAny>
        </AlertAny>
      ) : error ? (
        <AlertAny status="error" borderRadius="12px">
          <AlertIconAny />
          <AlertDescriptionAny>{error}</AlertDescriptionAny>
        </AlertAny>
      ) : !backtest ? (
        <AlertAny status="warning" borderRadius="12px">
          <AlertIconAny />
          <AlertDescriptionAny>Simulator not found.</AlertDescriptionAny>
        </AlertAny>
      ) : (
        <VStackAny align="stretch" spacing={4}>
          <Card>
            <CardHeader pb={2}>
              <FlexAny align="center" justify="space-between" wrap="wrap" gap={2}>
                <BoxAny>
                  <TextAny fontSize="lg" fontWeight="semibold" color={textColor}>
                    Details
                  </TextAny>
                  <TextAny fontSize="sm" color={subtleText}>
                    {statusBadge}
                  </TextAny>
                </BoxAny>
                <HStackAny>
                  <ButtonAny size="sm" variant="outline" onClick={() => void load()} isDisabled={saving}>
                    Refresh
                  </ButtonAny>
                  <ButtonAny size="sm" variant="outline" onClick={() => void onToggleArchive()} isLoading={saving}>
                    {status === "archived" ? "Restore" : "Archive"}
                  </ButtonAny>
                  <ButtonAny
                    size="sm"
                    variant="outline"
                    onClick={() => void onSaveAsStrategyVersion()}
                    isLoading={savingStrategy}
                    isDisabled={!canSaveAsStrategyVersion}
                    title={
                      isPolicyVersionSaved
                        ? "This simulation is already saved as a policy version"
                        : !linkedReportId
                          ? "Link a report id to enable policy versioning."
                          : !reportStrategyId
                            ? "This linked report has no strategy_id (or it failed to load)."
                            : "Save this simulator's policy as a new policy version (set as current)."
                    }
                  >
                    {isPolicyVersionSaved ? "Saved as policy version" : "Save as policy version"}
                  </ButtonAny>
                  <ButtonAny size="sm" colorScheme="teal" onClick={() => void onSave()} isLoading={saving} isDisabled={!canSave}>
                    Save
                  </ButtonAny>
                  <ButtonAny
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={() => void onDeleteBacktest()}
                    isLoading={deleting}
                    isDisabled={saving || deleting}
                  >
                    Delete
                  </ButtonAny>
                </HStackAny>
              </FlexAny>
            </CardHeader>
            <CardBody pt={0}>
              <VStackAny align="stretch" spacing={3}>
                <BoxAny>
                  <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                    Title
                  </TextAny>
                  <InputAny value={title} onChange={(e: any) => setTitle(e.target.value)} placeholder="e.g. April tuning run" />
                </BoxAny>
                <BoxAny>
                  <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                    Linked report id (optional)
                  </TextAny>
                  <InputAny value={reportId} onChange={(e: any) => setReportId(e.target.value)} placeholder="UUID" fontFamily="mono" />
                  {backtest.report_id ? (
                    <HStackAny mt={2} spacing={2}>
                      <ButtonAny size="sm" variant="ghost" onClick={() => history.push(`/reports/${encodeURIComponent(backtest.report_id!)}`)}>
                        Open linked report
                      </ButtonAny>
                    </HStackAny>
                  ) : null}
                </BoxAny>
                <DividerAny borderColor={borderColor} />

                <BoxAny>
                  <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                    Pinescript (strategy policy version)
                  </TextAny>
                  <ButtonAny
                    variant="link"
                    size="xs"
                    onClick={onTogglePine}
                    px={0}
                    h="auto"
                    minH={0}
                    fontWeight="normal"
                    isDisabled={!reportStrategyId}
                    title={!reportStrategyId ? "Link this simulator to a report with a strategy to enable Pinescript." : ""}
                  >
                    {pineOpen ? "Hide Pinescript" : "Show Pinescript"}
                  </ButtonAny>
                  <CollapseAny in={pineOpen} animateOpacity>
                    <BoxAny mt={2} borderWidth="1px" borderRadius="12px" borderColor={borderColor} p={3}>
                      {!reportStrategyId ? (
                        <TextAny color={subtleText} fontSize="sm">
                          Link this simulator to a report (with `strategy_id`) to enable Pinescript generation.
                        </TextAny>
                      ) : strategyDetailError ? (
                        <AlertAny status="error" borderRadius="md" fontSize="sm">
                          <AlertIconAny />
                          <AlertDescriptionAny>{strategyDetailError}</AlertDescriptionAny>
                        </AlertAny>
                      ) : strategyDetailLoading && !strategyDetail ? (
                        <HStackAny spacing={3}>
                          <SpinnerAny size="sm" />
                          <TextAny color={subtleText}>Loading strategy…</TextAny>
                        </HStackAny>
                      ) : (
                        <VStackAny align="stretch" spacing={3}>
                          <BoxAny>
                            <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                              Policy version
                            </TextAny>
                            <BoxAny
                              as="select"
                              value={selectedPolicyVersionId}
                              onChange={(e: any) => setSelectedPolicyVersionId(e.target.value)}
                              borderWidth="1px"
                              borderRadius="10px"
                              borderColor={borderColor}
                              px={3}
                              py={2}
                              fontSize="sm"
                              bg="transparent"
                            >
                              {(Array.isArray(strategyDetail?.policy_versions) ? strategyDetail.policy_versions : []).map((v: any) => {
                                const isCurrent =
                                  strategyDetail?.current_policy_version_id && v?.id === strategyDetail.current_policy_version_id;
                                const verNo = typeof v?.version_number === "number" ? `v${v.version_number}` : null;
                                const srcName =
                                  typeof v?.source_backtest_title === "string" && v.source_backtest_title.trim()
                                    ? v.source_backtest_title.trim()
                                    : null;
                                const title = typeof v?.title === "string" && v.title.trim() ? v.title.trim() : null;
                                const base = title || srcName || v?.id;
                                const label = `${isCurrent ? "Current • " : ""}${verNo ? `${verNo} • ` : ""}${base}${
                                  srcName && title ? ` • ${srcName}` : ""
                                }`;
                                return (
                                  <option key={v.id} value={v.id}>
                                    {label}
                                  </option>
                                );
                              })}
                            </BoxAny>
                          </BoxAny>

                          <HStackAny spacing={2} flexWrap="wrap">
                            <ButtonAny
                              size="sm"
                              colorScheme="teal"
                              onClick={() => void onGeneratePine()}
                              isLoading={pineLoading}
                              isDisabled={!selectedPolicyVersionId || pineLoading}
                            >
                              Generate
                            </ButtonAny>
                            {pineScript ? (
                              <ButtonAny size="sm" variant="outline" onClick={() => void copyPine()}>
                                Copy code
                              </ButtonAny>
                            ) : null}
                          </HStackAny>

                          {pineError ? (
                            <AlertAny status="error" borderRadius="md" fontSize="sm">
                              <AlertIconAny />
                              <AlertDescriptionAny>
                                {pineError}
                                {(pineCompileUnsupportedPaths.length > 0 || pineCompileUnsupportedOps.length > 0) && (
                                  <BoxAny mt={2}>
                                    {pineCompileUnsupportedPaths.length > 0 ? (
                                      <BoxAny mb={2}>
                                        <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                                          Unsupported paths
                                        </TextAny>
                                        <BoxAny as="pre" fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" m={0}>
                                          {pineCompileUnsupportedPaths.join("\n")}
                                        </BoxAny>
                                      </BoxAny>
                                    ) : null}
                                    {pineCompileUnsupportedOps.length > 0 ? (
                                      <BoxAny>
                                        <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                                          Unsupported ops
                                        </TextAny>
                                        <BoxAny as="pre" fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" m={0}>
                                          {pineCompileUnsupportedOps.join("\n")}
                                        </BoxAny>
                                      </BoxAny>
                                    ) : null}
                                  </BoxAny>
                                )}
                              </AlertDescriptionAny>
                            </AlertAny>
                          ) : null}

                          {pineScript ? (
                            <PineScriptCodeBlock code={pineScript} maxH="420px" />
                          ) : (
                            <TextAny color={subtleText} fontSize="sm">
                              Click “Generate” to fetch Pinescript for the selected strategy policy version.
                            </TextAny>
                          )}
                        </VStackAny>
                      )}
                    </BoxAny>
                  </CollapseAny>
                </BoxAny>

                {shownMetrics ? (
                  <BoxAny>
                    <BoxAny mb={3}>
                      <ResearchDisclaimerNotice kind="simulator" />
                    </BoxAny>
                    <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                      Results
                    </TextAny>
                    <StackAny spacing={3}>
                      <SimpleGridAny columns={{ base: 1, md: 2 }} gap={3}>
                        <MetricColumn title="Baseline" metric={shownMetrics.baseline} textColor={textColor} subtleColor={subtleText} compact />
                        <MetricColumn title="Filtered" metric={shownMetrics.filtered} textColor={textColor} subtleColor={subtleText} compact />
                      </SimpleGridAny>

                      {shownMetrics.delta && Object.keys(shownMetrics.delta).length > 0 ? (
                        <BoxAny borderWidth="1px" borderRadius="md" p={2}>
                          <TextAny fontWeight="semibold" color={textColor} mb={2} fontSize="xs" textTransform="uppercase">
                            Delta
                          </TextAny>
                          <StackAny spacing={1}>
                            {Object.entries(shownMetrics.delta).map(([k, v]) => (
                              <BoxAny key={k} display="flex" justifyContent="space-between" gap={3} fontSize="xs">
                                <TextAny color={subtleText}>{k}</TextAny>
                                <TextAny color={textColor} fontWeight="medium" textAlign="right">
                                  {formatMetricValue(k, v)}
                                </TextAny>
                              </BoxAny>
                            ))}
                          </StackAny>
                        </BoxAny>
                      ) : null}

                      {shownMetrics.coverage ? (
                        <BoxAny borderWidth="1px" borderRadius="md" p={2}>
                          <TextAny fontWeight="semibold" color={textColor} mb={2} fontSize="xs" textTransform="uppercase">
                            Coverage
                          </TextAny>
                          <StackAny spacing={1} fontSize="xs">
                            {shownMetrics.coverage.trades_total != null ? (
                              <BoxAny display="flex" justifyContent="space-between" gap={3}>
                                <TextAny color={subtleText}>In scope</TextAny>
                                <TextAny color={textColor} fontWeight="medium">
                                  {formatMetricValue("trades_total", shownMetrics.coverage.trades_total)}
                                </TextAny>
                              </BoxAny>
                            ) : null}
                            {shownMetrics.coverage.trades_with_entry_bar != null ? (
                              <BoxAny display="flex" justifyContent="space-between" gap={3}>
                                <TextAny color={subtleText}>With entry bar</TextAny>
                                <TextAny color={textColor} fontWeight="medium">
                                  {formatMetricValue("trades_with_entry_bar", shownMetrics.coverage.trades_with_entry_bar)}
                                </TextAny>
                              </BoxAny>
                            ) : null}
                            {shownMetrics.coverage.trades_missing_entry_bar != null ? (
                              <BoxAny display="flex" justifyContent="space-between" gap={3}>
                                <TextAny color={subtleText}>Missing entry bar</TextAny>
                                <TextAny color={textColor} fontWeight="medium">
                                  {formatMetricValue("trades_missing_entry_bar", shownMetrics.coverage.trades_missing_entry_bar)}
                                </TextAny>
                              </BoxAny>
                            ) : null}
                          </StackAny>
                        </BoxAny>
                      ) : null}
                    </StackAny>
                  </BoxAny>
                ) : null}

                {shownPolicy != null ? (
                  <BoxAny>
                    <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                      Policy
                    </TextAny>
                    <ButtonAny variant="link" size="xs" onClick={onTogglePolicy} px={0} h="auto" minH={0} fontWeight="normal">
                      {policyOpen ? "Hide policy" : "Show policy"}
                    </ButtonAny>
                    <CollapseAny in={policyOpen} animateOpacity>
                      <BoxAny mt={2} borderWidth="1px" borderRadius="12px" borderColor={evidencePreBorder} bg={evidencePreBg} p={3}>
                        {policyDisplay ? (
                          <StackAny spacing={2}>
                            <TextAny fontSize="sm" color={textColor} fontWeight="semibold">
                              {policyDisplay.headline}
                            </TextAny>
                            {policyDisplay.groups.length > 1 ? (
                              <TextAny fontSize="xs" color={subtleText}>
                                Match <strong>any</strong> of the following groups:
                              </TextAny>
                            ) : (
                              <TextAny fontSize="xs" color={subtleText}>
                                Match <strong>all</strong> of the following:
                              </TextAny>
                            )}
                            <StackAny spacing={3}>
                              {policyDisplay.groups.map((g, idx) => (
                                <BoxAny key={idx} borderWidth="1px" borderRadius="10px" borderColor={borderColor} bg="transparent" p={2}>
                                  {policyDisplay.groups.length > 1 ? (
                                    <TextAny fontSize="xs" color={subtleText} mb={1} fontWeight="semibold" textTransform="uppercase">
                                      Group {idx + 1}
                                    </TextAny>
                                  ) : null}
                                  <StackAny spacing={1}>
                                    {g.map((line, i) => (
                                      <TextAny key={i} fontSize="sm" color={textColor}>
                                        • {line}
                                      </TextAny>
                                    ))}
                                  </StackAny>
                                </BoxAny>
                              ))}
                            </StackAny>
                            <TextAny fontSize="xs" color={subtleText}>
                              Technical JSON is available under “Raw (read-only)”.
                            </TextAny>
                          </StackAny>
                        ) : (
                          <BoxAny
                            as="pre"
                            fontSize="xs"
                            fontFamily="mono"
                            whiteSpace="pre-wrap"
                            wordBreak="break-word"
                            m={0}
                            maxH="260px"
                            overflowY="auto"
                          >
                            {pretty(shownPolicy)}
                          </BoxAny>
                        )}
                      </BoxAny>
                    </CollapseAny>
                  </BoxAny>
                ) : null}

                <BoxAny>
                  <TextAny fontSize="xs" color={subtleText} textTransform="uppercase" mb={1}>
                    Raw (read-only)
                  </TextAny>
                  <ButtonAny variant="link" size="xs" onClick={onToggleRaw} px={0} h="auto" minH={0} fontWeight="normal">
                    {rawOpen ? "Hide raw" : "Show raw"}
                  </ButtonAny>
                  <CollapseAny in={rawOpen} animateOpacity>
                    <BoxAny
                      as="pre"
                      borderWidth="1px"
                      borderRadius="12px"
                      borderColor={borderColor}
                      p={3}
                      fontSize="xs"
                      overflow="auto"
                      whiteSpace="pre"
                      maxH="280px"
                      mt={2}
                    >
                      {pretty(backtest)}
                    </BoxAny>
                  </CollapseAny>
                </BoxAny>
              </VStackAny>
            </CardBody>
          </Card>

          <Card>
            <CardHeader pb={2}>
              <FlexAny align="center" justify="space-between" wrap="wrap" gap={2}>
                <BoxAny>
                  <TextAny fontSize="lg" fontWeight="semibold" color={textColor}>
                    Trade Review
                  </TextAny>
                  <TextAny fontSize="sm" color={subtleText}>
                    Overlay which trades this policy kept vs removed (for validation).
                  </TextAny>
                </BoxAny>
                <HStackAny>
                  <ButtonAny
                    size="sm"
                    variant={overlayEnabled ? "solid" : "outline"}
                    colorScheme="teal"
                    onClick={() => void onToggleOverlay()}
                    isLoading={tradeIdsLoading}
                    isDisabled={!backtest?.id}
                  >
                    {overlayEnabled ? "Overlay: ON" : "Overlay kept vs removed"}
                  </ButtonAny>
                </HStackAny>
              </FlexAny>
            </CardHeader>
            <CardBody pt={0} display="flex" flexDirection="column" alignItems="stretch" width="100%">
              {tradeIdsError ? (
                <AlertAny status="error" borderRadius="md" fontSize="sm" mb={3}>
                  <AlertIconAny />
                  <AlertDescriptionAny>{tradeIdsError}</AlertDescriptionAny>
                </AlertAny>
              ) : null}
              {overlayEnabled && tradeIds ? (
                <TextAny fontSize="xs" color={subtleText} mb={3} w="100%" whiteSpace="normal" wordBreak="break-word">
                  Overlay source: {tradeIds.source || "—"} · kept {tradeIds.kept.length} · removed {tradeIds.removed.length}
                  {tradeIds.source === "recomputed" ? " · first load may take longer while the server recomputes." : ""}
                </TextAny>
              ) : null}
              <TradeReviewTab
                brokerAccountCode={brokerAccountCodeForReview}
                tradesForSymbols={tradesForReview}
                requestWindow={reviewWindow as any}
                // Optional linked report — candles resolve via backtest_id (do not
                // send report_id as the sole cohort; missing reports 404 candles).
                reportIdForCohort={undefined}
                cohortIntent="backtest"
                backtestId={backtest.id}
                symbolDiscovery={backtest?.id ? { kind: "backtest", id: backtest.id } : undefined}
                recordedTimezoneLabel={tradeReviewRecordedTimezoneLabel || undefined}
                policyOverlay={
                  overlayEnabled && tradeIds
                    ? { kept_trade_ids: tradeIds.kept, removed_trade_ids: tradeIds.removed }
                    : null
                }
              />
            </CardBody>
          </Card>

          <NotesPanel entityType="backtest" entityId={backtest.id} />
        </VStackAny>
      )}
    </BoxAny>
  );
}

